import * as React from 'react'
import { Helmet } from 'react-helmet'
import { RouteComponentProps } from 'react-router'
import { AppState } from '../store'
import { AnyAction, Dispatch } from 'redux'
import { setCurrentUser, updateSettings, updateWorkspace } from '../store/authentication/actions'
import { connect } from 'react-redux'
import { SettingsController, WorkspaceController, OnboardingController, UserController, VATController, ActiveStorageController } from '../controllers'
import Notification from '../utilities/Notification'
import MoneyInput from '../components/Form/MoneyInput'
import { ISettingsForm } from '../controllers/SettingsController'
import TimeFormatter from '../utilities/TimeFormatter'
import TimeHelper from '../helpers/TimeHelper'
import NumberFormatter from '../utilities/NumberFormatter'
import { Body } from '../components/Body/Body'
import ERoute from '../ERoute'
import AvatarEditor from '../components/Avatar/AvatarEditor'
import ScrollToTopOnMount from '../components/Effects/ScrollToTopOnMount'
import PageLoader from '../components/Page/PageLoader'
import { withTranslation, WithTranslation } from 'react-i18next'
import { setLocale } from '../translations'
import LocaleHelper from '../helpers/LocaleHelper'
import DataLayerHelper, { DataLayerEventType } from '../helpers/DataLayerHelper'
import VATHelper from '../helpers/VatHelper'
import { CurrentUser, NavigationMenuItemPage, NavigationMenuItemType, PageNavigationMenuItem, Settings, Workspace } from '../types'
import Images from '../images'
import Icon, { IconTypes } from '../components/Icons/Icon'
import { ChevronLeft, Info } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Field, FieldLabel } from '@/components/ui/field'
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip'

// The legacy Sprockets stylesheet (loaded app-wide via application.html.erb, unlike the shadcn-only
// `login` layout) declares bare, unlayered `label` / `input, select, textarea` rules that beat
// Tailwind's layered utilities regardless of specificity. Force the shadcn look with `!important`
// (Tailwind v4 trailing-`!` syntax) on just the properties that actually collide.
const LABEL_OVERRIDE_CLASSNAME = 'flex! mb-0! text-sm! font-medium! text-foreground! normal-case! font-sans!'
const INPUT_OVERRIDE_CLASSNAME = 'rounded-lg! border-input! bg-transparent! px-2.5! py-1! font-sans!'

const MONEY_INPUT_CLASSNAME = `h-8 w-full min-w-0 border ${INPUT_OVERRIDE_CLASSNAME} text-base transition-colors outline-none placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm dark:bg-input/30`

interface OnboardingFeature {
  pages: NavigationMenuItemPage[]
  label: string
  description: string
  icon: IconTypes
}

interface IStateToProps {
  currentUser: CurrentUser
}

interface IDispatchToProps {
  setCurrentUser: typeof setCurrentUser
  updateSettings: typeof updateSettings
  updateWorkspace: typeof updateWorkspace
}

type IProps = IStateToProps & IDispatchToProps & RouteComponentProps<any> & WithTranslation

interface IWelcomeStep {
  render: () => JSX.Element
  completed: boolean
}

interface IState {
  currentUser: CurrentUser
  workspace: Workspace
  setting: Settings
  isSubmitting: boolean
  steps: IWelcomeStep[]
  form: ISettingsForm | null
  didInitialLoad: boolean
  errors: any
}

class Onboarding extends React.Component<IProps, IState> {
  private fileUpload = React.createRef<HTMLInputElement>()

  constructor(props: IProps) {
    super(props)

    this.state = {
      currentUser: props.currentUser,
      workspace: props.currentUser.workspace,
      setting: props.currentUser.workspace.setting,
      isSubmitting: false,
      errors: {},
      steps: [
        { render: () => this.renderStep1(), completed: false },
        { render: () => this.renderStep2(), completed: false },
        { render: () => this.renderStep3(), completed: false },
        { render: () => this.renderStep4(), completed: false },
        { render: () => this.renderStep5(), completed: false },
      ],
      form: null,
      didInitialLoad: false
    }

    this.onFullNameChange = this.onFullNameChange.bind(this)
    this.onSubmitPress = this.onSubmitPress.bind(this)
    this.onLocaleChange = this.onLocaleChange.bind(this)
    this.onCountryChange = this.onCountryChange.bind(this)
    this.onTimezoneChange = this.onTimezoneChange.bind(this);
    this.onDateformatChange = this.onDateformatChange.bind(this);
    this.onTimeFormatChange = this.onTimeFormatChange.bind(this)
    this.onDefaultCurrencyChange = this.onDefaultCurrencyChange.bind(this);
    this.onNumberFormatChange = this.onNumberFormatChange.bind(this);
    this.onBusinessNameChange = this.onBusinessNameChange.bind(this);
    this.onBusinessVatNumberChange = this.onBusinessVatNumberChange.bind(this);
    this.onDefaultHourlyRateChange = this.onDefaultHourlyRateChange.bind(this);
    this.onDefaultDayRateChange = this.onDefaultDayRateChange.bind(this);
    this.onQuotationPrefixNumberChange = this.onQuotationPrefixNumberChange.bind(this)
    this.onQuotationNumberChange = this.onQuotationNumberChange.bind(this)
    this.onQuotationSuffixNumberChange = this.onQuotationSuffixNumberChange.bind(this)
    this.onQuotationValidityDurationChange = this.onQuotationValidityDurationChange.bind(this)
    this.onInvoicePrefixNumberChange = this.onInvoicePrefixNumberChange.bind(this)
    this.onInvoiceNumberChange = this.onInvoiceNumberChange.bind(this)
    this.onInvoiceSuffixNumberChange = this.onInvoiceSuffixNumberChange.bind(this)
    this.onInvoicePaymentDurationChange = this.onInvoicePaymentDurationChange.bind(this);
    this.onAddLogoClick = this.onAddLogoClick.bind(this)
    this.onDeleteLogoClick = this.onDeleteLogoClick.bind(this)
    this.onLogoChange = this.onLogoChange.bind(this)
    this.onStreetChange = this.onStreetChange.bind(this)
    this.onCityChange = this.onCityChange.bind(this)
    this.onZipcodeChange = this.onZipcodeChange.bind(this)
    this.onToggleFeatureClick = this.onToggleFeatureClick.bind(this)
    this.navigateBack = this.navigateBack.bind(this)
    this.navigateForward = this.navigateForward.bind(this)
    this.onBackPress = this.onBackPress.bind(this)
    this.finishSetup = this.finishSetup.bind(this)
  }

  componentWillMount() {
    const { currentUser, history } = this.props

    if (currentUser.workspace.did_onboarding) {
      history.push(ERoute.PATH_DASHBOARD)
    }
  }

  componentDidMount() {
    OnboardingController
      .getForm()
      .then((form) => {
        this.setState({
          form: form,
          didInitialLoad: true
        })
      })
      .catch(console.error)

    DataLayerHelper.push({ event: DataLayerEventType.SIGNUP })
  }

  updateCurrentUser(callback) {
    const { currentUser } = this.state
    const { setCurrentUser, t } = this.props

    UserController
      .updateUser(currentUser)
      .then(response => {
        const { errors } = response;

        if (errors) {
          this.setState({
            errors: errors
          });

          Notification.notifyError(t('Onboarding::Oops something went wrong!'));
        }
        else {
          this.setState({
            currentUser: response,
          });

          // Set current user state
          setCurrentUser(response)

          // Add new locale
          setLocale(response.locale)

          if (callback) callback()
        }
      })
      .catch(console.error)
  }

  updateSettings(callback) {
    const { setting } = this.state
    const { updateSettings, t } = this.props

    SettingsController
      .update(setting)
      .then(response => {
        const { errors } = response;

        if (errors) {
          this.setState({
            errors: errors
          });

          Notification.notifyError(t('Onboarding::Oops something went wrong!'));
        }
        else {
          this.setState({
            setting: response,
          });

          updateSettings(setting)

          if (callback) callback()
        }
      })
      .catch(console.error)
  }

  updateWorkspace(callback) {
    const { workspace } = this.state
    const { updateWorkspace } = this.props

    WorkspaceController
      .update(workspace)
      .then(response => {
        const { errors } = response;

        if (errors) {
          this.setState({
            errors: errors
          });
        }
        else {
          this.setState({
            workspace: workspace
          });

          updateWorkspace(workspace)

          if (callback) callback()
        }
      })
      .catch(console.error)
  }

  getCurrentStepIndex() {
    const { steps } = this.state

    const index = steps.findIndex(step => !step.completed)

    if (index === -1) {
      return steps.length - 1
    }

    return index
  }

  onSubmitPress(e) {
    e.preventDefault()

    this.updateCurrentUser(() => {
      this.updateSettings(() => {
        this.updateWorkspace(() => {
          this.navigateForward()
        })
      })
    })
  }

  onBackPress(e) {
    e.preventDefault()
    this.navigateBack()
  }

  onFullNameChange(e) {
    const { currentUser } = this.state

    this.setState({
      currentUser: {
        ...currentUser,
        name: e.currentTarget.value
      }
    })
  }

  onLocaleChange(locale) {
    const { currentUser } = this.state;

    this.setState({
      currentUser: {
        ...currentUser,
        locale: locale,
      }
    }, () => {
      setLocale(this.state.currentUser.locale)
    });
  }

  onCountryChange(countryCode) {
    const { workspace } = this.state;

    this.setState({
      workspace: {
        ...workspace,
        country_code: countryCode,
      }
    })
  }

  onTimezoneChange(timezone) {
    const { workspace } = this.state;

    workspace.timezone = timezone;

    this.setState({
      workspace: workspace
    });
  }

  onDateformatChange(dateFormat) {
    const { setting } = this.state;

    setting.date_format = dateFormat;

    this.setState({
      setting: setting
    });
  }

  onTimeFormatChange(timeFormat) {
    const { setting } = this.state;

    setting.time_format = timeFormat;

    this.setState({
      setting: setting
    });
  }

  onDefaultCurrencyChange(currency) {
    const { setting } = this.state;

    setting.default_currency = currency;

    this.setState({
      setting: setting,
    });
  }

  onNumberFormatChange(numberFormat) {
    const { setting } = this.state;

    setting.number_format = numberFormat;

    this.setState({
      setting: setting
    });
  }

  onBusinessNameChange(e) {
    const businessName = e.currentTarget.value;

    const { workspace } = this.state;

    workspace.business_name = businessName;

    this.setState({
      workspace: workspace
    });
  }

  onBusinessVatNumberChange(e) {
    e.preventDefault()
    const { workspace } = this.state;

    const vatNumber = e.currentTarget.value;


    this.setState({
      workspace: {
        ...workspace,
        vat_number: vatNumber
      }
    }, async () => {

      if (VATHelper.isValidEUVAT(vatNumber)) {
        try {
          const response = await VATController.getDetails(vatNumber)

          if (!response.errors) {
            const { name, city, country, postal, street, street_number } = response

            this.setState({
              workspace: {
                ...this.state.workspace,
                business_name: name,
                street: street ? `${street} ${street_number}` : '',
                country_code: country,
                zip_code: postal,
                city: city,
              }
            })
          }
        } catch (ex) {
          console.error(ex)
        }
      }

    });
  }

  onQuotationPrefixNumberChange(e) {
    const prefixQuotationNumber = e.currentTarget.value;

    const { setting } = this.state;

    this.setState({
      setting: {
        ...setting,
        prefix_quotation_number: prefixQuotationNumber
      },
    });
  }

  onQuotationNumberChange(e) {
    const quotationNumber = e.currentTarget.value;

    const { setting } = this.state;

    this.setState({
      setting: {
        ...setting,
        quotation_number: quotationNumber,
      },
    });
  }

  onQuotationSuffixNumberChange(e) {
    const suffixQuotationNumber = e.currentTarget.value;

    const { setting } = this.state;

    this.setState({
      setting: {
        ...setting,
        suffix_quotation_number: suffixQuotationNumber,
      },
    });
  }

  onDefaultHourlyRateChange(value) {
    const { setting } = this.state;

    this.setState({
      setting: {
        ...setting,
        default_hourly_rate: value
      }
    });
  }

  onDefaultDayRateChange(value) {
    const { setting } = this.state;

    this.setState({
      setting: {
        ...setting,
        default_day_rate: value
      }
    });
  }

  onQuotationValidityDurationChange(e) {
    const { setting } = this.state;

    const validityDuration = e.currentTarget.value;

    setting.quotation_validity_duration = validityDuration;

    this.setState({
      setting: setting
    });
  }

  onInvoicePrefixNumberChange(e) {
    const prefixInvoiceNumber = e.currentTarget.value;

    const { setting } = this.state;

    this.setState({
      setting: {
        ...setting,
        prefix_invoice_number: prefixInvoiceNumber
      },
    });
  }

  onInvoiceNumberChange(e) {
    const quotationNumber = e.currentTarget.value;

    const { setting } = this.state;

    this.setState({
      setting: {
        ...setting,
        invoice_number: quotationNumber,
      },
    });
  }

  onInvoiceSuffixNumberChange(e) {
    const suffixInvoiceNumber = e.currentTarget.value;

    const { setting } = this.state;

    this.setState({
      setting: {
        ...setting,
        suffix_invoice_number: suffixInvoiceNumber,
      },
    });
  }


  onInvoicePaymentDurationChange(e) {
    const paymentDuration = e.currentTarget.value;

    const { setting } = this.state;

    setting.invoice_payment_duration = paymentDuration;

    this.setState({
      setting: setting
    });
  }

  onAddLogoClick(e) {
    e.preventDefault();
    this.fileUpload.current.click();
  }

  async onDeleteLogoClick() {
    const { workspace } = this.state
    const { updateWorkspace } = this.props

    try {
      const updatedWorkspace = await WorkspaceController.update({
        ...workspace,
        business_logo: null
      })

      this.setState({ workspace: updatedWorkspace })
      updateWorkspace(updatedWorkspace)
    } catch (ex) {
      console.error(ex)
    }
  }

  onLogoChange(acceptedFiles) {
    const { updateWorkspace } = this.props
    const logo = acceptedFiles[0];

    try {
      ActiveStorageController.uploadPublic(logo, async (error, blob) => {
        if (error) { console.error(error) }
        if (blob) {
          const { url } = await ActiveStorageController.getBlobUrl(blob)

          const updatedWorkspace = await WorkspaceController.update({ ...this.state.workspace, business_logo: blob.signed_id })

          updateWorkspace(updatedWorkspace)

          this.setState({ workspace: updatedWorkspace })
        }
      })
    } catch (ex) {
      console.error(ex)
    }
  }

  onStreetChange(e) {
    const street = e.currentTarget.value;

    const { workspace } = this.state;

    workspace.street = street;

    this.setState({
      workspace: workspace
    });
  }

  onCityChange(e) {
    const city = e.currentTarget.value;

    const { workspace } = this.state;

    workspace.city = city;

    this.setState({
      workspace: workspace
    });
  }

  onZipcodeChange(e) {
    const { workspace } = this.state;
    const zipCode = e.currentTarget.value;

    workspace.zip_code = zipCode;

    this.setState({
      workspace: workspace
    });
  }

  onToggleFeatureClick(feature: OnboardingFeature) {
    const { setting } = this.state

    const activePageMenuItems = setting
      .default_menu
      .filter(menuItem => menuItem.type === NavigationMenuItemType.PAGE)
      .map((menuItem) => (menuItem as PageNavigationMenuItem).page)

    if (feature.pages.every(page => activePageMenuItems.includes(page))) {
      // Disable
      setting.default_menu = setting.default_menu
        .filter(menuItem => {
          return !(menuItem.type === NavigationMenuItemType.PAGE && feature.pages.includes(menuItem.page))
        })
    } else {
      // Add
      feature.pages.forEach(page => {
        const menuItem: PageNavigationMenuItem = { type: NavigationMenuItemType.PAGE, page: page }

        // Booking page belongs directly under the calendar
        const calendarIndex = setting.default_menu.findIndex(item => item.type === NavigationMenuItemType.PAGE && item.page === NavigationMenuItemPage.CALENDAR)

        if (page === NavigationMenuItemPage.SCHEDULING && calendarIndex !== -1) {
          setting.default_menu.splice(calendarIndex + 1, 0, menuItem)
        } else {
          setting.default_menu.push(menuItem)
        }
      })
    }

    this.setState({ setting: { ...setting, } })
  }

  finishSetup() {
    const { history, updateWorkspace } = this.props

    OnboardingController
      .finish()
      .then(response => {
        updateWorkspace(response)
        history.push(ERoute.PATH_DASHBOARD)
      })
  }

  navigateBack() {
    const { steps } = this.state
    const currentStepIndex = this.getCurrentStepIndex()

    if (currentStepIndex - 1 >= 0) {
      steps[currentStepIndex - 1].completed = false
      this.setState({ steps })
    }
  }

  navigateForward() {
    const { steps } = this.state
    const beforeStepIndex = this.getCurrentStepIndex()

    if (beforeStepIndex <= steps.length - 1) {

      steps[beforeStepIndex].completed = true

      this.setState({ steps: [...steps] }, () => {
        const newStepIndex = this.getCurrentStepIndex()

        if (beforeStepIndex === newStepIndex) {
          this.finishSetup()
        }
      })
    }
  }

  renderStepShell(options: {
    title: string
    description: string
    showBack: boolean
    nextLabel: string
    children: React.ReactNode
  }) {
    const { t } = this.props
    const { steps } = this.state
    const { title, description, showBack, nextLabel, children } = options
    const currentStepIndex = this.getCurrentStepIndex()
    const isLastStep = currentStepIndex === steps.length - 1

    return (
      <div className="flex w-full flex-col overflow-hidden rounded-2xl border border-border bg-card md:flex-row">
        <div className="flex flex-col justify-between gap-10 bg-muted/40 p-8 md:p-10 flex-1">
          <div>
            {showBack && (
              <button
                type="button"
                onClick={this.onBackPress}
                className="mb-6 inline-flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
              >
                <ChevronLeft className="size-4" />
                {t('Onboarding::Previous')}
              </button>
            )}

            <h1 className="text-2xl font-bold! tracking-tight text-foreground md:text-3xl">
              {title}
            </h1>
            <p className="mt-3 text-sm text-muted-foreground md:text-base">
              {description}
            </p>
          </div>

          <div className="flex items-center gap-1.5">
            {steps.map((_, index) => (
              <span
                key={index}
                className={cn(
                  'h-1.5 rounded-full transition-all',
                  index === currentStepIndex ? 'w-6 bg-foreground' : 'w-1.5 bg-foreground/20'
                )}
              />
            ))}
          </div>
        </div>

        <div className="flex flex-col gap-1 p-8 md:p-10 flex-1">
          {children}

          <div className="flex flex-col gap-2 mt-6">
            <Button size="lg" className="w-full" onClick={this.onSubmitPress}>
              {nextLabel}
            </Button>

            {!isLastStep && (
              <Button variant="link" size="lg" className="w-full" onClick={this.onSubmitPress}>
                {t('Onboarding::Skip')}
              </Button>
            )}
          </div>
        </div>
      </div>
    )
  }

  renderStep1() {
    const { t } = this.props
    const { currentUser, workspace, form } = this.state

    const localeOptions = LocaleHelper.getLocaleOptions()

    return this.renderStepShell({
      title: t('Onboarding::Nice to meet you'),
      description: t('Onboarding::Just enough to say hello properly.'),
      showBack: false,
      nextLabel: t('Onboarding::Next'),
      children: (
        <div className="flex flex-col gap-4">
          <Field className="gap-2">
            <FieldLabel htmlFor="onboarding-full-name" className={LABEL_OVERRIDE_CLASSNAME}>{t('Onboarding::Full name')}</FieldLabel>
            <Input
              id="onboarding-full-name"
              type="text"
              value={currentUser.name}
              onChange={this.onFullNameChange}
              placeholder={t('Onboarding::Full name')}
              className={INPUT_OVERRIDE_CLASSNAME}
            />
          </Field>

          <div className="flex flex-col gap-4 sm:flex-row">
            <Field className="flex-1 gap-2">
              <FieldLabel htmlFor="onboarding-locale" className={LABEL_OVERRIDE_CLASSNAME}>{t('Onboarding::Language')}</FieldLabel>
              <Select items={localeOptions} value={currentUser.locale} onValueChange={(value: string) => this.onLocaleChange(value)}>
                <SelectTrigger id="onboarding-locale" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {localeOptions.map(locale => (
                    <SelectItem key={locale.value} value={locale.value}>{locale.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <Field className="flex-1 gap-2">
              <FieldLabel htmlFor="onboarding-country" className={LABEL_OVERRIDE_CLASSNAME}>{t('Onboarding::Country')}</FieldLabel>
              <Select items={form.countries} value={workspace.country_code} onValueChange={(value: string) => this.onCountryChange(value)}>
                <SelectTrigger id="onboarding-country" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {form.countries.map(country => (
                    <SelectItem key={country.value} value={country.value}>{country.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>
        </div>
      ),
    })
  }

  renderStep2() {
    const { t } = this.props
    const { setting, form, workspace } = this.state

    return this.renderStepShell({
      title: t('Onboarding::There\'s a time and place for everything'),
      description: t('Onboarding::Timezones, currencies, date formats — because 03/04/25 means three different dates depending who you ask.'),
      showBack: true,
      nextLabel: t('Onboarding::Next'),
      children: (
        <div className="flex flex-col gap-4">
          <Field className="gap-2">
            <FieldLabel htmlFor="onboarding-timezone" className={LABEL_OVERRIDE_CLASSNAME}>{t('Onboarding::Timezone')}</FieldLabel>
            <Select items={form.time_zones} value={workspace.timezone} onValueChange={(value: string) => this.onTimezoneChange(value)}>
              <SelectTrigger id="onboarding-timezone" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {form.time_zones.map((timezone, index) => (
                  <SelectItem key={`${timezone.value}-${index}`} value={timezone.value}>{timezone.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <Field className="gap-2">
            <FieldLabel htmlFor="onboarding-currency" className={LABEL_OVERRIDE_CLASSNAME}>{t('Onboarding::Currency')}</FieldLabel>
            <Select items={form.currencies} value={setting.default_currency} onValueChange={(value: string) => this.onDefaultCurrencyChange(value)}>
              <SelectTrigger id="onboarding-currency" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {form.currencies.map((currency, index) => (
                  <SelectItem key={`${currency.value}-${index}`} value={currency.value}>{currency.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <Field className="gap-2">
            <FieldLabel htmlFor="onboarding-date-format" className={LABEL_OVERRIDE_CLASSNAME}>{t('Onboarding::Date format')}</FieldLabel>
            <Select value={setting.date_format} onValueChange={(value: string) => this.onDateformatChange(value)}>
              <SelectTrigger id="onboarding-date-format" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {form.date_formats.map((format, index) => (
                  <SelectItem key={`${format}-${index}`} value={format}>{format}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <Field className="gap-2">
            <FieldLabel htmlFor="onboarding-time-format" className={LABEL_OVERRIDE_CLASSNAME}>{t('Onboarding::Time format')}</FieldLabel>
            <Select items={form.time_formats.map(format => ({ value: format, label: TimeFormatter.durationFormat(TimeHelper.getCurrentTimeInSeconds(), format) }))} value={setting.time_format} onValueChange={(value: string) => this.onTimeFormatChange(value)}>
              <SelectTrigger id="onboarding-time-format" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {form.time_formats.map((format, index) => (
                  <SelectItem key={`${format}-${index}`} value={format}>
                    {TimeFormatter.durationFormat(TimeHelper.getCurrentTimeInSeconds(), format)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <Field className="gap-2">
            <FieldLabel htmlFor="onboarding-number-format" className={LABEL_OVERRIDE_CLASSNAME}>{t('Onboarding::Number format')}</FieldLabel>
            <Select items={form.currency_formats.map(format => ({ value: format, label: NumberFormatter.formatLabels[format] }))} value={setting.number_format} onValueChange={(value: string) => this.onNumberFormatChange(value)}>
              <SelectTrigger id="onboarding-number-format" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {form.currency_formats.map(format => (
                  <SelectItem key={format} value={format}>{NumberFormatter.formatLabels[format]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        </div>
      ),
    })
  }

  renderStep3() {
    const { t } = this.props
    const { workspace } = this.state

    return this.renderStepShell({
      title: t('Onboarding::The boring bit'),
      description: t('Onboarding::Give us your VAT number and we\'ll autofill the boring stuff. Logo optional, but highly encouraged.'),
      showBack: true,
      nextLabel: t('Onboarding::Next'),
      children: (
        <div className="flex flex-col gap-4">
          <div className="mb-2">
            <AvatarEditor
              uri={workspace.business_logo_url}
              onChange={this.onLogoChange}
              onDelete={this.onDeleteLogoClick}
            />
          </div>

          <Field className="gap-2">
            <FieldLabel htmlFor="onboarding-vat-number" className={LABEL_OVERRIDE_CLASSNAME}>{t('Onboarding::VAT number')}</FieldLabel>
            <Input
              id="onboarding-vat-number"
              type="text"
              name="vat_number"
              value={workspace.vat_number}
              placeholder={t('Onboarding::VAT number')}
              onChange={this.onBusinessVatNumberChange}
              className={INPUT_OVERRIDE_CLASSNAME}
            />
          </Field>

          <Field className="gap-2">
            <FieldLabel htmlFor="onboarding-business-name" className={LABEL_OVERRIDE_CLASSNAME}>{t('Onboarding::Company name')}</FieldLabel>
            <Input
              id="onboarding-business-name"
              type="text"
              name="business_name"
              value={workspace.business_name}
              placeholder={t('Onboarding::Company name')}
              onChange={this.onBusinessNameChange}
              className={INPUT_OVERRIDE_CLASSNAME}
            />
          </Field>

          <Field className="gap-2">
            <FieldLabel htmlFor="onboarding-street" className={LABEL_OVERRIDE_CLASSNAME}>{t('CompanyInfo::Street')}</FieldLabel>
            <Input
              id="onboarding-street"
              type="text"
              name="street"
              value={workspace.street}
              placeholder={t('CompanyInfo::Street')}
              onChange={this.onStreetChange}
              className={INPUT_OVERRIDE_CLASSNAME}
            />
          </Field>

          <div className="flex flex-col gap-4 sm:flex-row">
            <Field className="gap-2">
              <FieldLabel htmlFor="onboarding-city" className={LABEL_OVERRIDE_CLASSNAME}>{t('CompanyInfo::City')}</FieldLabel>
              <Input
                id="onboarding-city"
                type="text"
                name="city"
                value={workspace.city}
                placeholder={t('CompanyInfo::City')}
                onChange={this.onCityChange}
                className={INPUT_OVERRIDE_CLASSNAME}
              />
            </Field>

            <Field className="gap-2">
              <FieldLabel htmlFor="onboarding-zip-code" className={LABEL_OVERRIDE_CLASSNAME}>{t('CompanyInfo::Postal')}</FieldLabel>
              <Input
                id="onboarding-zip-code"
                type="text"
                name="zip_code"
                value={workspace.zip_code}
                placeholder={t('CompanyInfo::Postal')}
                onChange={this.onZipcodeChange}
                className={INPUT_OVERRIDE_CLASSNAME}
              />
            </Field>
          </div>
        </div>
      ),
    })
  }

  renderStep4() {
    const { t } = this.props
    const { setting } = this.state

    return this.renderStepShell({
      title: t('Onboarding::Let\'s talk numbers'),
      description: t('Onboarding::Set your rates, then decide how your invoices number themselves. 0 is a fine place to start.'),
      showBack: true,
      nextLabel: t('Onboarding::Next'),
      children: (
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-4 sm:flex-row">
            <Field className="gap-2">
              <FieldLabel htmlFor="onboarding-hourly-rate" className={LABEL_OVERRIDE_CLASSNAME}>
                {t('Onboarding::Hourly rate')}
                <Tooltip>
                  <TooltipTrigger>
                    <Info className="size-3.5 text-muted-foreground" />
                  </TooltipTrigger>
                  <TooltipContent>
                    {t('Onboarding::We use this rate when you start tracking working hours in {{__appName}}')}
                  </TooltipContent>
                </Tooltip>
              </FieldLabel>
              <MoneyInput
                name="default_hourly_rate"
                className={MONEY_INPUT_CLASSNAME}
                currency={setting.default_currency}
                numberFormat={setting.number_format}
                placeholderValue={40}
                value={setting.default_hourly_rate}
                onBlur={this.onDefaultHourlyRateChange}
              />
            </Field>

            <Field className="gap-2">
              <FieldLabel htmlFor="onboarding-day-rate" className={LABEL_OVERRIDE_CLASSNAME}>
                {t('Onboarding::Day rate')}
                <Tooltip>
                  <TooltipTrigger>
                    <Info className="size-3.5 text-muted-foreground" />
                  </TooltipTrigger>
                  <TooltipContent>
                    {t('Onboarding::We use this rate when you start tracking working days in {{__appName}}')}
                  </TooltipContent>
                </Tooltip>
              </FieldLabel>
              <MoneyInput
                name="default_day_rate"
                className={MONEY_INPUT_CLASSNAME}
                currency={setting.default_currency}
                numberFormat={setting.number_format}
                placeholderValue={450}
                value={setting.default_day_rate}
                onBlur={this.onDefaultDayRateChange}
              />
            </Field>
          </div>

          <Field className="gap-2">
            <FieldLabel className={LABEL_OVERRIDE_CLASSNAME}>{t('Onboarding::Quotation number format')}</FieldLabel>
            <div className="flex gap-2">
              <Input
                type="text"
                name="prefix"
                placeholder={t('Onboarding::Prefix')}
                value={setting.prefix_quotation_number}
                onChange={this.onQuotationPrefixNumberChange}
                className={cn(INPUT_OVERRIDE_CLASSNAME, 'w-24!')}
              />
              <Input
                type="number"
                name="number"
                placeholder="1"
                value={setting.quotation_number}
                onChange={this.onQuotationNumberChange}
                pattern="\d*"
                required
                className={cn(INPUT_OVERRIDE_CLASSNAME, 'flex-1')}
              />
              <Input
                type="text"
                name="suffix"
                placeholder={t('Onboarding::Suffix')}
                value={setting.suffix_quotation_number}
                onChange={this.onQuotationSuffixNumberChange}
                className={cn(INPUT_OVERRIDE_CLASSNAME, 'w-24!')}
              />
            </div>
          </Field>

          <Field className="gap-2">
            <FieldLabel className={LABEL_OVERRIDE_CLASSNAME}>{t('Onboarding::Invoice number format')}</FieldLabel>
            <div className="flex gap-2">
              <Input
                type="text"
                name="prefix"
                placeholder={t('Onboarding::Prefix')}
                value={setting.prefix_invoice_number}
                onChange={this.onInvoicePrefixNumberChange}
                className={cn(INPUT_OVERRIDE_CLASSNAME, 'w-24!')}
              />
              <Input
                type="number"
                name="number"
                placeholder="1"
                value={setting.invoice_number}
                onChange={this.onInvoiceNumberChange}
                pattern="\d*"
                required
                className={cn(INPUT_OVERRIDE_CLASSNAME, 'flex-1')}
              />
              <Input
                type="text"
                name="suffix"
                placeholder={t('Onboarding::Suffix')}
                value={setting.suffix_invoice_number}
                onChange={this.onInvoiceSuffixNumberChange}
                className={cn(INPUT_OVERRIDE_CLASSNAME, 'w-24!')}
              />
            </div>
          </Field>
        </div>
      ),
    })
  }

  renderStep5() {
    const { t } = this.props
    const { setting } = this.state

    const activePageMenuItems = setting
      .default_menu
      .filter(menuItem => menuItem.type === NavigationMenuItemType.PAGE)
      .map((menuItem) => (menuItem as PageNavigationMenuItem).page)

    const features: OnboardingFeature[] = [
      { pages: [NavigationMenuItemPage.INVOICES, NavigationMenuItemPage.BANK], label: t('Onboarding::Invoice and payments'), description: t('Onboarding::Create professional looking invoices in seconds and let users pay online.'), icon: 'invoice' },
      { pages: [NavigationMenuItemPage.DEALS], label: t('Onboarding::Deals'), description: t('Onboarding::Manage and visualize your sales cycle from prospect to sale with deals.'), icon: 'check-circle' },
      { pages: [NavigationMenuItemPage.EXPENSES], label: t('Onboarding::Expense management'), description: t('Onboarding::Track and categorize your expenses automatically using our AI powered assistant.'), icon: 'receipt' },
      { pages: [NavigationMenuItemPage.TASKS], label: t('Onboarding::Task management'), description: t('Onboarding::Create and delegate todo\'s, link them to contacts in our collaborative online workspace.'), icon: 'boards' },
      { pages: [NavigationMenuItemPage.PROJECTS], label: t('Onboarding::Project management'), icon: 'project', description: t('Onboarding::Manage simple to complex projects and everything in between with our interactive project management tool.') },
      { pages: [NavigationMenuItemPage.TIME_TRACKING], label: t('Onboarding::Timesheets'), description: t('Onboarding::Track time from anywhere and visualise time entries in a powerful timesheet.'), icon: 'stopwatch' },
      { pages: [NavigationMenuItemPage.PRODUCTS], label: t('Onboarding::Products and materials'), description: t('Onboarding::Manage a list of products with corresponding stock and easily invoice them to contacts.'), icon: 'product' },
      { pages: [NavigationMenuItemPage.FILES], label: t('Onboarding::File management'), description: t('Onboarding::Easy to use, private, and secure file management for all your documents.'), icon: 'folder' },
      { pages: [NavigationMenuItemPage.PROPOSALS], label: t('Onboarding::Proposal builder'), description: t('Onboarding::Create eye-catching dynamic proposals. Track when they are seen and receive instant notifications when they are approved.'), icon: 'proposal' },
      { pages: [NavigationMenuItemPage.CONTRACTS], label: t('Onboarding::Contract builder'), description: t('Onboarding::Send and track contracts or legal documents. Get them signed by one or multiple parties.'), icon: 'file-contract' },
      { pages: [NavigationMenuItemPage.FORMS], label: t('Onboarding::Form builder'), description: t('Onboarding::Collect information through beautiful and responsive forms that can be shared with a link or embedded on your website.'), icon: 'input-pipe' },
      { pages: [NavigationMenuItemPage.SCHEDULING], label: t('Onboarding::Booking page'), description: t('Onboarding::Share your booking page and let clients schedule meetings based on your availability, synced with your calendar.'), icon: 'calendar-clock' },
    ]

    return this.renderStepShell({
      title: t('Onboarding::What brings you to {{__appName}}?'),
      description: t('Onboarding::Pick the tools you want to play with. Commitment-free — swap them anytime in settings.'),
      showBack: true,
      nextLabel: t('Onboarding::Finish'),
      children: (
        <div className="flex flex-col gap-3">
          {features.map(feature => {
            const checked = feature.pages.every(page => activePageMenuItems.includes(page))

            return (
              <div
                key={feature.label}
                onClick={() => this.onToggleFeatureClick(feature)}
                className="flex cursor-pointer items-start gap-3 rounded-lg border border-border p-3 transition-colors hover:bg-muted/50"
              >
                <span className="flex size-9 shrink-0 items-center justify-center rounded-md bg-muted text-foreground">
                  <Icon icon={feature.icon} className="size-4" />
                </span>

                <span className="flex-1">
                  <span className="block text-sm font-medium text-foreground">{feature.label}</span>
                  <span className="block text-sm text-muted-foreground">{feature.description}</span>
                </span>

                <Checkbox checked={checked} className="pointer-events-none mt-1" />
              </div>
            )
          })}
        </div>
      ),
    })
  }

  render() {
    const { t } = this.props
    const { didInitialLoad, steps } = this.state
    const currentStepIndex = this.getCurrentStepIndex()

    return (
      <div className="flex min-h-full w-full items-center justify-center overflow-x-hidden overflow-y-auto bg-background p-4 font-sans! md:p-8">
        <Helmet>
          <title>{t('Onboarding::{{__appName}} | Onboarding')}</title>
        </Helmet>

        <ScrollToTopOnMount />

        <div className="flex w-full max-w-4xl flex-col items-center gap-8 py-8">
          <a href="/">
            <img src={Images.LOGO_TEXT} className="h-8 w-auto" />
          </a>

          {!didInitialLoad && <PageLoader />}
          {didInitialLoad && steps[currentStepIndex].render()}
        </div>
      </div>
    )
  }
}

const mapStateToProps = (state: AppState): IStateToProps => {
  const {
    authentication: {
      currentUser,
    }
  } = state

  return {
    currentUser: currentUser,
  }
}


const mapDispatchToProps = (dispatch: Dispatch<AnyAction>): IDispatchToProps => {
  return {
    setCurrentUser: (user: CurrentUser) => dispatch(setCurrentUser(user)),
    updateSettings: (settings: Settings) => dispatch(updateSettings(settings)),
    updateWorkspace: (workspace: Workspace) => dispatch(updateWorkspace(workspace)),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(Onboarding))